import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@localagent/ui/lib/utils';
import { Tooltip } from '@localagent/ui/components/tooltip';
import { NotificationNavBadge } from './notifications/NotificationNavBadge';

function NavGlyph({ children }: { children: ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

const NAV_ITEMS: Array<{ path: string; label: string; icon: ReactNode; testId?: string; alsoActiveUnder?: string }> = [
  {
    path: '/feed',
    label: 'Live Feed',
    testId: 'nav-live-feed',
    icon: (
      <>
        <path d="M4 11a9 9 0 0 1 9 9" />
        <path d="M4 4a16 16 0 0 1 16 16" />
        <circle cx="5" cy="19" r="1" />
      </>
    ),
  },
  {
    path: '/chat',
    label: 'Chat',
    icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  },
  {
    path: '/agents',
    label: 'Agents',
    alsoActiveUnder: '/personas',
    icon: (
      <>
        <path d="M12 8V4H8" />
        <rect width="16" height="12" x="4" y="8" rx="2" />
        <path d="M2 14h2" />
        <path d="M20 14h2" />
        <path d="M15 13v2" />
        <path d="M9 13v2" />
      </>
    ),
  },
  {
    path: '/work',
    label: 'Work',
    icon: (
      <>
        <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        <rect width="20" height="14" x="2" y="6" rx="2" />
      </>
    ),
  },
  {
    path: '/search',
    label: 'Search',
    icon: (
      <>
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
      </>
    ),
  },
  {
    path: '/artifacts',
    label: 'Artifacts',
    testId: 'nav-artifacts',
    icon: (
      <>
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
        <path d="M8 13h8" />
        <path d="M8 17h8" />
      </>
    ),
  },
  {
    path: '/orgchart',
    label: 'Org Chart',
    icon: (
      <>
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
        <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
      </>
    ),
  },
  {
    path: '/connections',
    label: 'Connections',
    icon: (
      <>
        <path d="M12 22v-5" />
        <path d="M9 8V2" />
        <path d="M15 8V2" />
        <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
      </>
    ),
  },
  {
    path: '/workspaces',
    label: 'Workspaces',
    icon: (
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    ),
  },
];

export function Navigation({ compact = false }: { compact?: boolean }) {
  const { pathname } = useLocation();
  return (
    <nav
      className={cn('flex flex-col gap-0.5', compact ? 'items-center px-1 py-1' : 'p-2')}
      data-compact={compact ? 'true' : 'false'}
    >
      {NAV_ITEMS.map((item) => {
        const link = (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={item.testId ?? `nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              cn(
                'flex items-center rounded-md transition-colors',
                compact
                  ? 'relative justify-center p-2'
                  : 'px-2.5 py-1.5 text-label font-medium',
                isActive || (item.alsoActiveUnder !== undefined && (pathname === item.alsoActiveUnder || pathname.startsWith(`${item.alsoActiveUnder}/`)))
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )
            }
          >
            {compact ? <NavGlyph>{item.icon}</NavGlyph> : <span>{item.label}</span>}
            {item.path === '/feed' && <NotificationNavBadge compact={compact} />}
          </NavLink>
        );

        if (!compact) return link;

        return (
          <Tooltip key={item.path} label={item.label} side="right">
            {link}
          </Tooltip>
        );
      })}
    </nav>
  );
}
